import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faPaperPlane, 
  faUser, 
  faFileInvoice, 
  faCreditCard, 
  faPhone, 
  faExclamationTriangle,
  faChevronRight,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

const RejectionMessageForm = ({ application, onSubmit, onCancel, isLoading }) => {
  const [reason, setReason] = useState('The amount requested exceeds our current lending limits.');
  const [charCount, setCharCount] = useState(reason.length);
  const [selectedQuickReason, setSelectedQuickReason] = useState(0);

  const handleSubmit = () => {
    onSubmit(reason);
  };

  const handleReasonChange = (e) => {
    const value = e.target.value;
    if (value.length <= 200) {
      setReason(value);
      setCharCount(value.length);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const rejectionReasons = [
    { 
      title: 'Amount exceeds limits', 
      description: 'The amount requested exceeds our current lending limits.',
      icon: faCreditCard 
    },
    { 
      title: 'Insufficient credit history', 
      description: 'Insufficient credit history or score.',
      icon: faFileInvoice 
    },
    { 
      title: 'Inadequate income', 
      description: 'Inadequate income verification.',
      icon: faUser 
    },
    { 
      title: 'High debt ratio', 
      description: 'High debt-to-income ratio.',
      icon: faExclamationTriangle 
    },
    { 
      title: 'Incomplete documents', 
      description: 'Incomplete application documentation.',
      icon: faFileInvoice 
    },
    { 
      title: 'Not meeting criteria', 
      description: 'Does not meet current lending criteria.',
      icon: faTimes 
    }
  ];

  const handleQuickReasonSelect = (index, description) => {
    setSelectedQuickReason(index);
    setReason(description);
    setCharCount(description.length);
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-1 z-50 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[90vh] overflow-y-auto border border-gray-100">
        {/* Elegant Header */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900"></div>
          <div className="relative px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center shadow-inner">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  className="text-red-500 text-lg" 
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white tracking-tight">Reject Application</h2>
                <p className="text-sm text-gray-300 font-light mt-0.5">
                  Application ID: <span className="font-mono font-medium">{application.applicationId}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-all duration-200 group"
              aria-label="Close"
            >
              <FontAwesomeIcon 
                icon={faTimes} 
                className="text-gray-300 group-hover:text-white text-sm transition-colors" 
              />
            </button>
          </div>
        </div>

        {/* Application Summary Card */}
        <div className="px-6 pt-6">
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="text-emerald-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Borrower</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{application.borrowerName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faCreditCard} className="text-blue-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Product</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{application.loanProductName}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-purple-50 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faPhone} className="text-purple-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Contact</p>
                    <p className="text-sm font-semibold text-gray-800">{application.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileInvoice} className="text-amber-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Application ID</p>
                    <p className="text-sm font-mono font-semibold text-gray-800">{application.applicationId}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rejection Content */}
        <div className="px-6 py-5">
          <div className="space-y-5">
            {/* Quick Reasons Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  Select Quick Reason
                </label>
                <span className="text-xs font-medium text-gray-500">
                  {charCount}/200 characters
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                {rejectionReasons.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleQuickReasonSelect(index, item.description)}
                    className={`p-3 rounded-xl border transition-all duration-200 flex items-center justify-between group ${
                      selectedQuickReason === index 
                        ? 'border-red-300 bg-gradient-to-r from-red-50 to-red-25 shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selectedQuickReason === index 
                          ? 'bg-gradient-to-br from-red-500 to-red-400' 
                          : 'bg-gray-100 group-hover:bg-gray-200'
                      }`}>
                        <FontAwesomeIcon 
                          icon={item.icon} 
                          className={`text-sm ${
                            selectedQuickReason === index ? 'text-white' : 'text-gray-600'
                          }`} 
                        />
                      </div>
                      <span className={`text-xs font-medium text-left ${
                        selectedQuickReason === index ? 'text-red-700' : 'text-gray-700'
                      }`}>
                        {item.title}
                      </span>
                    </div>
                    {selectedQuickReason === index && (
                      <FontAwesomeIcon 
                        icon={faCheck} 
                        className="text-red-500 text-xs" 
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Message Section */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Custom Message
              </label>
              
              <div className="relative">
                <textarea
                  value={reason}
                  onChange={handleReasonChange}
                  disabled={isLoading}
                  className="w-full px-4 py-4 rounded-xl border border-gray-300 focus:border-gray-400 focus:ring-2 focus:ring-gray-200 focus:ring-opacity-50 resize-none transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[120px] text-sm placeholder-gray-400"
                  placeholder="Provide a clear, professional, and empathetic rejection message..."
                  maxLength={200}
                />
                <div className="absolute bottom-3 right-3">
                  <div className={`text-xs font-medium px-2 py-1 rounded ${
                    charCount > 180 
                      ? 'bg-red-100 text-red-700' 
                      : charCount > 150 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {charCount}/200
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="flex-1 px-4 py-3.5 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 group"
              >
                <FontAwesomeIcon icon={faTimes} className="text-gray-500 group-hover:text-gray-700" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || !reason.trim()}
                className="flex-1 px-4 py-3.5 rounded-xl bg-gradient-to-r from-blue-800 to-blue-900 hover:from-red-600 hover:to-red-700 text-white font-semibold shadow-lg hover:shadow-xl hover:shadow-red-500/30 active:scale-[0.98] disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2.5 group"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="text-sm group-hover:translate-x-0.5 transition-transform" />
                    <span>Send Rejection</span>
                    <FontAwesomeIcon icon={faChevronRight} className="text-xs opacity-70 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Elegant Footer */}
        <div className="border-t border-gray-100 px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 font-medium">
              <span className="text-red-500">⚠️</span> This action cannot be undone
            </p>
            <p className="text-xs text-gray-400">
              Borrower will be notified via email & SMS
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionMessageForm;